/**
 * Visible Trade ID normalization.
 *
 * The visible Trade ID (T1, T2, T3, ...) is a compact display ordinal for the
 * account-level trade collection. It is NOT the record's canonical identity.
 *
 * The whole persisted collection is re-numbered gaplessly (T1..Tn) whenever the
 * collection changes (import, manual create, broker-sync insert, deletion, clear,
 * duplicate repair, or local/server merge), using a deterministic stable order:
 *
 *   1. opened/execution timestamp ascending
 *   2. createdAt/importedAt ascending
 *   3. immutable internal ID (storageId/id) as the final tie-breaker
 *
 * Internal IDs and source trade IDs are preserved verbatim and never overwritten.
 * The table renders each persisted trade's stored visible `tradeId` directly and
 * never recalculates it during sorting/filtering/pagination.
 */

import { normalizeTimestampToMs } from '../time/normalizeTimestampToMs';

export function parseVisibleTradeNumber(tradeId: unknown): number | null {
  const text = String(tradeId ?? '').trim().toUpperCase();
  const match = /^T(\d+)$/.exec(text);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) && value >= 1 ? value : null;
}

export function formatVisibleTradeId(number: number): string {
  return `T${Math.max(1, Math.floor(number))}`;
}

/** Resolves the record's opened/execution timestamp in ms (0 when unavailable). */
export function resolveTradeOpenTimestampMs(record: any): number {
  if (!record || typeof record !== 'object') return 0;
  const candidates = [
    record.openTimestampMs,
    record.openTime,
    record.timestamp,
    record.entryTime,
    record.entryTimestamp,
    record.executedAt,
    record.filledAt,
    record.time,
    record.date,
  ];
  for (const candidate of candidates) {
    const normalized = normalizeTimestampToMs(candidate);
    if (normalized != null && Number.isFinite(normalized) && normalized > 0) return normalized;
  }
  const executionsList = Array.isArray(record.executionsList) ? record.executionsList : [];
  for (const execution of executionsList) {
    for (const key of ['timestamp', 'time', 'executedAt']) {
      const normalized = normalizeTimestampToMs(execution?.[key]);
      if (normalized != null && Number.isFinite(normalized) && normalized > 0) return normalized;
    }
    if (executionsList.length > 0) break;
  }
  return 0;
}

/**
 * Deterministic stable order for the whole persisted collection:
 * opened/execution timestamp asc, then createdAt/importedAt asc, then internal ID.
 */
function stableVisibleIdOrder(records: any[]): Array<{ record: any; index: number }> {
  return records
    .map((record, index) => ({ record, index }))
    .sort((a, b) => {
      const aOpen = resolveTradeOpenTimestampMs(a.record);
      const bOpen = resolveTradeOpenTimestampMs(b.record);
      if (aOpen !== bOpen) return aOpen - bOpen;
      const aTime = Number(a.record?.createdAt ?? a.record?.importedAt ?? 0);
      const bTime = Number(b.record?.createdAt ?? b.record?.importedAt ?? 0);
      if (aTime !== bTime) return aTime - bTime;
      return String(a.record?.storageId ?? a.record?.id ?? '').localeCompare(
        String(b.record?.storageId ?? b.record?.id ?? ''),
      );
    });
}

/**
 * Re-numbers the COMPLETE persisted trade collection to a single gapless sequence
 * T1..Tn. Runs after every collection mutation (import, manual create,
 * broker-sync insert, deletion, clear, duplicate repair, local/server merge).
 *
 * - Assigns T1..Tn across the ENTIRE final collection (never per-batch, never
 *   "highest existing + 1", never raw CSV row index, never incoming trade_id).
 * - Preserves each record's original array position (only `tradeId` is replaced).
 * - Deterministic and idempotent: running it twice reports zero changes.
 * - Internal IDs and source trade IDs are untouched.
 */
export function normalizeVisibleTradeIds(records: any[], options?: { total?: number | null }): { records: any[]; changed: number } {
  const source = Array.isArray(records) ? records : [];
  const ordered = stableVisibleIdOrder(source);
  const total = Number.isFinite(Number(options?.total)) ? Number(options?.total) : null;
  const offset = total != null && total > source.length ? total - source.length : 0;

  let changed = 0;
  const result = new Array(source.length);
  ordered.forEach(({ record, index }, ordinal) => {
    const visibleId = formatVisibleTradeId(ordinal + 1 + offset);
    const next = { ...record, tradeId: visibleId };
    if (String(record?.tradeId ?? '') !== visibleId) changed += 1;
    result[index] = next;
  });

  return { records: result, changed };
}

/**
 * Placeholder batch labeler used by the import flows before the final save.
 *
 * The server's temporary trade IDs are replaced here with a simple T1..Tn
 * placeholder range for the incoming batch. The authoritative gapless sequence is
 * computed by `normalizeVisibleTradeIds` over the COMPLETE persisted collection at
 * the persistence boundary, so these placeholder values are always overwritten and
 * never become the final visible IDs.
 */
export function allocateVisibleTradeIds(_existingRecords: any[], incomingRecords: any[]): any[] {
  return (Array.isArray(incomingRecords) ? incomingRecords : []).map((record, index) => ({
    ...record,
    tradeId: formatVisibleTradeId(index + 1),
  }));
}
